import { FC } from "react";
import { Text } from "@chakra-ui/react";
import { type PlanType } from "sdk/types";
import {
  chatGuidelinesDocUrl,
  coachLinkTreeUrl,
  nurseLinkTreeUrl,
  routes,
} from "~/constants";
import { trackHeader as track } from "~/events";
import { useIsMobile } from "~/hooks/useIsMobile";
import { useMembershipType } from "~/hooks/useMembershipType";
import { useSubscriptionPlan } from "~/hooks/useSubscriptionPlan";
import {
  ChevronRight,
  HeartCircle,
  HeartHand,
  Logo,
  MedicalCross,
  Settings02,
  Stars02,
} from "~/icons";
import { Trans } from "~/utils/i18next";
import { AvatarMenu } from "./AvatarMenu";
import { BasicNavItem } from "./BasicNavItem";
import { MessageConceiveNavItem } from "./MessageConceiveNavItem";
import { MessageCrewNavItem } from "./MessageCrewNavItem";
import { MobileDivider } from "./MobileDivider";
import { MyCareTeamMenu } from "./MyCareTeamMenu";
import { SignOutNavItem } from "./SignOutNavItem";
import { SignUpNavItem } from "./SignUpNavItem";
import { NavItemProps, type INavItem } from "./types";
import { useMobileDrawerContent } from "./useMobileDrawerContent";

const account: INavItem = {
  children: <Trans i18nKey="navigation.accountHeading" />,
  Component: BasicNavItem,
  drawerContent: "account",
  right: <ChevronRight />,
};

const avatar: INavItem = {
  Component: AvatarMenu,
};

const myCareTeam: INavItem = {
  Component: MyCareTeamMenu,
};

const careTeam: INavItem = {
  Component: BasicNavItem,
  drawerContent: "careTeam",
  children: <Trans i18nKey="navigation.careTeam" />,
  right: <ChevronRight />,
};

const dashboard: INavItem = {
  Component: BasicNavItem,
  children: <Trans i18nKey="navigation.home" />,
  onClick: track.newMemberHomeLink,
  to: routes.dashboard,
};

const divider: INavItem = {
  Component: MobileDivider as FC<NavItemProps>,
};

const howItWorks: INavItem = {
  Component: BasicNavItem,
  children: <Trans i18nKey="navigation.howItWorks" />,
  onClick: track.howItWorksLink,
  to: routes.howItWorks,
};

const chatGuidelinesDoc: INavItem = {
  Component: BasicNavItem,
  children: <Trans i18nKey="navigation.chatGuidelinesDoc" />,
  onClick: track.chatGuidelinesDoc,
  href: chatGuidelinesDocUrl,
};

const clinics: INavItem = {
  Component: BasicNavItem,
  children: <Trans i18nKey="navigation.clinics" />,
  onClick: track.clinicsLink,
  to: routes.clinics,
};

const library: INavItem = {
  Component: BasicNavItem,
  children: <Trans i18nKey="navigation.library" />,
  onClick: track.libraryLink,
  to: routes.library,
};

const gatherings: INavItem = {
  Component: BasicNavItem,
  children: <Trans i18nKey="navigation.gatherings" />,
  onClick: track.gatheringsLink,
  to: routes.gatherings,
};

const messageCrew: INavItem = {
  Component: MessageCrewNavItem,
};

const messageConceive: INavItem = {
  Component: MessageConceiveNavItem,
};

const referAFriend: INavItem = {
  children: <Trans i18nKey="navigation.referAFriend" />,
  Component: BasicNavItem,
  left: <HeartHand h={4} mr={2} w={4} />,
  onClick: track.referAFriendLink,
  to: routes.referAFriend,
};

const memberPerks: INavItem = {
  children: <Trans i18nKey="navigation.perks" />,
  Component: BasicNavItem,
  left: <Logo color="th.black" h={4} mr={2} w={4} />,
  onClick: track.perksLink,
  to: "https://sites.google.com/weconceive.com/memberperks/home",
};

const root: INavItem = {
  Component: BasicNavItem,
  children: <Trans i18nKey="navigation.home" />,
  onClick: track.homeLink,
  to: routes.root,
};

const settings: INavItem = {
  children: <Trans i18nKey="navigation.settings" />,
  Component: BasicNavItem,
  left: <Settings02 h={4} mr={2} w={4} />,
  onClick: track.settingsLink,
  to: routes.settings,
};

const signIn: INavItem = {
  Component: BasicNavItem,
  children: <Trans i18nKey="navigation.signIn" />,
  onClick: track.signInLink,
  to: routes.signIn,
};

const signOut: INavItem = {
  Component: SignOutNavItem,
};

const signUp: INavItem = {
  Component: SignUpNavItem,
};

const speakWithANursePlus: INavItem = {
  bottom: (
    <Text color="th.gray4" ml={6} mt={-2} variant="th-body3">
      <Trans i18nKey="navigation.speakWithANurse.plus.subheading" />
    </Text>
  ),
  children: <Trans i18nKey="navigation.speakWithANurse.plus.heading" />,
  Component: BasicNavItem,
  href: routes.onDemandCoaching,
  left: <MedicalCross h={4} mr={2} w={4} />,
  onClick: track.onDemandCoaching,
};

const speakWithANursePremium: INavItem = {
  ...speakWithANursePlus,
  children: <Trans i18nKey="navigation.speakWithANurse.premium.heading" />,
  bottom: null,
  href: nurseLinkTreeUrl,
  onClick: track.speakWithANurse,
};

const speakWithACoachPlus: INavItem = {
  bottom: (
    <Text color="th.gray4" ml={6} mt={-2} variant="th-body3">
      <Trans i18nKey="navigation.speakWithACoach.plus.subheading" />
    </Text>
  ),
  children: <Trans i18nKey="navigation.speakWithACoach.plus.heading" />,
  Component: BasicNavItem,
  href: routes.onDemandCoaching,
  left: <HeartCircle h={4} mr={2} strokeWidth={1} w={4} />,
  onClick: track.onDemandCoaching,
};

const speakWithACoachPremium: INavItem = {
  ...speakWithACoachPlus,
  bottom: null,
  href: coachLinkTreeUrl,
  onClick: track.speakWithACoach,
};

const onboardingV1UpgradeToPremium: INavItem = {
  children: <Trans i18nKey="navigation.upgradeToPremium" />,
  Component: BasicNavItem,
  left: <Stars02 h={4} mr={2} w={4} />,
  onClick: track.upgradeToPremium,
  state: { upgrade: true },
  to: routes.planSelect,
};

// --------------------------------------------

interface UseNavItems {
  leftNavItems: INavItem[];
  rightNavItems: INavItem[];
}

interface UseNavItemsByDrawerContent {
  default: UseNavItems;
  careTeam: UseNavItems;
  account: UseNavItems;
}

type Monitor = "desktop" | "mobile";

type NavItemsByMonitor = Record<Monitor, UseNavItems>;

type NavItemsByDrawerContentAndMonitor = Record<
  Monitor,
  UseNavItemsByDrawerContent
>;

const unauthenticatedNavItems: NavItemsByMonitor = {
  desktop: {
    leftNavItems: [],
    rightNavItems: [library, gatherings, howItWorks, clinics, signIn, signUp],
  },
  mobile: {
    leftNavItems: [],
    rightNavItems: [
      root,
      library,
      gatherings,
      howItWorks,
      clinics,
      divider,
      signIn,
      signUp,
    ],
  },
};

const authenticatedPlusDesktopItems = {
  leftNavItems: [dashboard, library, gatherings],
  rightNavItems: [myCareTeam, avatar],
};

const authenticatedPremiumDesktopItems = {
  leftNavItems: [dashboard, library, gatherings],
  rightNavItems: [myCareTeam, avatar],
};

const authenticatedPlusPregnancyDesktopItems = {
  leftNavItems: [dashboard],
  rightNavItems: [myCareTeam, avatar],
};

const authenticatedPremiumPregnancyDesktopItems = {
  leftNavItems: [dashboard],
  rightNavItems: [myCareTeam, avatar],
};

const authenticatedNavItems: Record<
  PlanType,
  NavItemsByDrawerContentAndMonitor
> = {
  plus: {
    desktop: {
      default: authenticatedPlusDesktopItems,
      careTeam: authenticatedPlusDesktopItems,
      account: authenticatedPlusDesktopItems,
    },
    mobile: {
      default: {
        leftNavItems: [dashboard, library, gatherings],
        rightNavItems: [careTeam, divider, account],
      },
      careTeam: {
        leftNavItems: [messageConceive, speakWithANursePlus],
        rightNavItems: [signOut],
      },
      account: {
        leftNavItems: [
          settings,
          onboardingV1UpgradeToPremium,
          referAFriend,
          memberPerks,
        ],
        rightNavItems: [signOut],
      },
    },
  },
  premium: {
    desktop: {
      default: authenticatedPremiumDesktopItems,
      careTeam: authenticatedPremiumDesktopItems,
      account: authenticatedPremiumDesktopItems,
    },
    mobile: {
      default: {
        leftNavItems: [dashboard, library, gatherings],
        rightNavItems: [careTeam, divider, account],
      },
      careTeam: {
        leftNavItems: [
          messageConceive,
          messageCrew,
          speakWithANursePremium,
          speakWithACoachPremium,
        ],
        rightNavItems: [chatGuidelinesDoc],
      },
      account: {
        leftNavItems: [settings, referAFriend, memberPerks],
        rightNavItems: [signOut],
      },
    },
  },
};

const authenticatedPregnancyNavItems: Record<
  PlanType,
  NavItemsByDrawerContentAndMonitor
> = {
  plus: {
    desktop: {
      default: authenticatedPlusPregnancyDesktopItems,
      careTeam: authenticatedPlusPregnancyDesktopItems,
      account: authenticatedPlusPregnancyDesktopItems,
    },
    mobile: {
      default: {
        leftNavItems: [dashboard],
        rightNavItems: [careTeam, divider, account],
      },
      careTeam: {
        leftNavItems: [messageConceive, speakWithANursePlus],
        rightNavItems: [signOut],
      },
      account: {
        leftNavItems: [
          settings,
          onboardingV1UpgradeToPremium,
          referAFriend,
          memberPerks,
        ],
        rightNavItems: [signOut],
      },
    },
  },
  premium: {
    desktop: {
      default: authenticatedPremiumPregnancyDesktopItems,
      careTeam: authenticatedPremiumPregnancyDesktopItems,
      account: authenticatedPremiumPregnancyDesktopItems,
    },
    mobile: {
      default: {
        leftNavItems: [dashboard],
        rightNavItems: [careTeam, divider, account],
      },
      careTeam: {
        leftNavItems: [
          messageConceive,
          messageCrew,
          speakWithANursePremium,
          speakWithACoachPremium,
        ],
        rightNavItems: [chatGuidelinesDoc],
      },
      account: {
        leftNavItems: [settings, referAFriend, memberPerks],
        rightNavItems: [signOut],
      },
    },
  },
};

export const useNavItems = (): UseNavItems => {
  const { plan, user } = useSubscriptionPlan();
  const drawerContent = useMobileDrawerContent();
  const { isFertility } = useMembershipType();
  const isMobile = useIsMobile();
  const authItems = isFertility
    ? authenticatedNavItems
    : authenticatedPregnancyNavItems;

  const monitor: Monitor = isMobile ? "mobile" : "desktop";

  if (!user) return unauthenticatedNavItems[monitor];

  const actualPlan = plan ?? "plus";

  const navItems = authItems[actualPlan][monitor][drawerContent];

  return navItems;
};
